import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { EmployeeProfile } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/fetchEmployeeProfiles/types";
import { fetchEmployeeProfiles } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/fetchEmployeeProfiles/fetchEmployeeProfilesApi";
import { fetchEmployeeProfile } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/fetchEmployeeProfile/fetchEmployeeProfileApi";
import {
  CreateEmployeeProfileRequest,
  CreateEmployeeProfileResponse,
} from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/createEmployeeProfile/types";
import { createEmployeeProfile } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/createEmployeeProfile/createEmployeeProfileApi";
import {
  UpdateEmployeeProfileRequest,
  UpdateEmployeeProfileResponse,
} from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/updateEmployeeProfile/types";
import { updateEmployeeProfile } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/updateEmployeeProfile/updateEmployeeProfileApi";
import { deleteEmployeeProfile } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Profile/deleteEmployeeProfile/deleteEmployeeProfileApi";

interface EmployeeProfileState {
  list: EmployeeProfile[];
  loadingList: boolean;
  listError: string | null;

  fetched: EmployeeProfile | null;
  loadingFetch: boolean;
  fetchError: string | null;

  loadingCreate: boolean;
  createError: string | null;

  loadingUpdate: boolean;
  updateError: string | null;

  loadingDelete: boolean;
  deleteError: string | null;
}

const initialState: EmployeeProfileState = {
  list: [],
  loadingList: false,
  listError: null,

  fetched: null,
  loadingFetch: false,
  fetchError: null,

  loadingCreate: false,
  createError: null,

  loadingUpdate: false,
  updateError: null,

  loadingDelete: false,
  deleteError: null,
};

const employeeProfileSlice = createSlice({
  name: "employeeProfile",
  initialState,
  reducers: {
    setList(state, action: PayloadAction<EmployeeProfile[]>) {
      state.list = action.payload;
    },
    setLoadingList(state, action: PayloadAction<boolean>) {
      state.loadingList = action.payload;
    },
    setListError(state, action: PayloadAction<string | null>) {
      state.listError = action.payload;
    },
    setFetched(state, action: PayloadAction<EmployeeProfile | null>) {
      state.fetched = action.payload;
    },
    setLoadingFetch(state, action: PayloadAction<boolean>) {
      state.loadingFetch = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setLoadingCreate(state, action: PayloadAction<boolean>) {
      state.loadingCreate = action.payload;
    },
    setCreateError(state, action: PayloadAction<string | null>) {
      state.createError = action.payload;
    },
    setLoadingUpdate(state, action: PayloadAction<boolean>) {
      state.loadingUpdate = action.payload;
    },
    setUpdateError(state, action: PayloadAction<string | null>) {
      state.updateError = action.payload;
    },
    setLoadingDelete(state, action: PayloadAction<boolean>) {
      state.loadingDelete = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
    resetFetched(state) {
      state.fetched = null;
      state.fetchError = null;
    },
  },
});

export const {
  setList,
  setLoadingList,
  setListError,
  setFetched,
  setLoadingFetch,
  setFetchError,
  setLoadingCreate,
  setCreateError,
  setLoadingUpdate,
  setUpdateError,
  setLoadingDelete,
  setDeleteError,
  resetFetched,
} = employeeProfileSlice.actions;

export default employeeProfileSlice.reducer;

export const fetchEmployeeProfilesAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setLoadingList(true));
    dispatch(setListError(null));
    try {
      const response = await fetchEmployeeProfiles();
      dispatch(setList(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch employee profiles.";
      dispatch(setListError(message));
    } finally {
      dispatch(setLoadingList(false));
    }
  };

export const fetchEmployeeProfileAction =
  (uuid: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingFetch(true));
    dispatch(setFetchError(null));
    try {
      const response = await fetchEmployeeProfile(uuid);
      dispatch(setFetched(response.data));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch employee profile.";
      dispatch(setFetchError(message));
    } finally {
      dispatch(setLoadingFetch(false));
    }
  };

export const createEmployeeProfileAction =
  (
    requestData: CreateEmployeeProfileRequest,
    onSuccess?: (created: CreateEmployeeProfileResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingCreate(true));
    dispatch(setCreateError(null));
    try {
      const response = await createEmployeeProfile(requestData);
      onSuccess?.(response.data);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create employee profile.";
      dispatch(setCreateError(message));
    } finally {
      dispatch(setLoadingCreate(false));
    }
  };

export const updateEmployeeProfileAction =
  (
    uuid: string,
    requestData: UpdateEmployeeProfileRequest,
    onSuccess?: (updated: UpdateEmployeeProfileResponse["data"]) => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingUpdate(true));
    dispatch(setUpdateError(null));
    try {
      const response = await updateEmployeeProfile(uuid, requestData);
      onSuccess?.(response.data);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update employee profile.";
      dispatch(setUpdateError(message));
    } finally {
      dispatch(setLoadingUpdate(false));
    }
  };

export const deleteEmployeeProfileAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingDelete(true));
    dispatch(setDeleteError(null));
    try {
      await deleteEmployeeProfile(uuid);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to delete employee profile.";
      dispatch(setDeleteError(message));
    } finally {
      dispatch(setLoadingDelete(false));
    }
  };
